import React, { useState, useEffect } from 'react';
import { logUserActivityQuietly } from './supabaseClient';
function AminoglycosideCalculator({ aminoDrug, aminoStrategy, user }) {
  // 1. STATES (This is Line 7 area - must be at the very top!)
  const [age, setAge] = useState('0');
  const [weight, setWeight] = useState('0');
  const [height, setHeight] = useState('0');
  const [scr, setScr] = useState(0);
  const [scrUnit, setScrUnit] = useState('umol'); // 'umol' or 'mgdl'
  const [gender, setGender] = useState('male');
  // Determine Constant based on Gender
  const crclConstant = gender === 'male' ? 1.23 : 1.04;
  // Add this in your math section (above return)
  const normalizedScr = scrUnit === 'mgdl' ? scr * 88.4 : scr;
  const [drawTime, setDrawTime] = useState('0.5'); // Default to 1 hour (standard peak time)
  const [manualTau, setManualTau] = useState('24');
  const [currentDose, setCurrentDose] = useState('0'); // Default dose in mg
  const [newDose, setNewDose] = useState('0'); // Default to a standard dose
  const [newTau, setNewTau] = useState('24'); // Default to Dosing interval
  
  useEffect(() => {
    if (aminoStrategy === 'extended') {
      setManualTau(24); // Default to Once Daily
    } else {
      setManualTau(8);  // Default to Q8H
    }
  }, [aminoStrategy]); // This "fires" whenever the user clicks the strategy buttons
  // This effect runs whenever the user clicks the "Extended" or "Traditional" buttons
  useEffect(() => {
    if (aminoStrategy === 'extended') {
      setNewTau(24); // Automatically suggest Once Daily
    } else {
      setNewTau(8);  // Automatically suggest Q8H
    }
  }, [aminoStrategy]); // This means: "Watch the aminoStrategy variable for changes"

  
  // 2. Weight Selection & Clinical Dosing Weight Logic (Official Protocol Engine)
  const weightNum = parseFloat(weight) || 0;
  const heightNum = parseFloat(height) || 0;

  let ibw = 0;
  let adjBw = 0;
  let weightToUse = weightNum; // Default fallback to ABW if no height is provided
  let weightSelectionMode = "⚠️ Actual (No Ht)"; // For display text string layout logs

  if (heightNum > 0) {
    // A. Compute official handbook IBW based on cm directly
    ibw = gender === 'male' 
      ? 50 + 0.9 * (heightNum - 152) 
      : 45.5 + 0.9 * (heightNum - 152);

    // B. Compute potential Adjusted Body Weight (AdjBW)
    adjBw = ibw + 0.4 * (weightNum - ibw);

    // C. Calculate the official clinical ratio (ABW / IBW) to check thresholds
    const weightRatio = ibw > 0 ? weightNum / ibw : 1;

    // D. Apply exact rules matching your expert's guidelines handbook layout:
    if (weightRatio > 1.2) {
      weightToUse = adjBw;
      weightSelectionMode = "Using Adjusted BW (>120% Obese)";
    } else if (weightRatio > 0.9 && weightRatio <= 1.2) {
      weightToUse = weightNum; // Uses ABW for normal weights
      weightSelectionMode = "Using Actual BW (Normal Weight)";
    } else if (weightRatio > 0.75 && weightRatio <= 0.9) {
      weightToUse = ibw;
      weightSelectionMode = "Using IBW (Underweight Tier)";
    } else if (weightRatio <= 0.75) {
      weightToUse = weightNum * 1.13;
      weightSelectionMode = "Using ABW × 1.13 (Severe Cachexia Tier)";
    }
  }
  
  // 3. Calculate CrCl (ml/min) using the Dosing Weight from Step 1
  const crcl = normalizedScr > 0 ? ((140 - age) * weightToUse * crclConstant) / normalizedScr: 0;
 
  // 4: SET TARGETS BASED ON STRATEGY ---
  let targetCmax, targetCmin;
  if (aminoStrategy === 'extended') {
    // Extended Interval (Single Daily) Targets
    targetCmax = aminoDrug === 'gentamicin' ? 20 : 60; // Genta vs Amikacin
    targetCmin = 0.5; 
  } else {
    // Traditional (Multiple Daily) Targets
    targetCmax = aminoDrug === 'gentamicin' ? 8 : 25; 
    targetCmin = aminoDrug === 'gentamicin' ? 1 : 5;
  }

  // Add these to your useState section
  const [measuredPeak, setMeasuredPeak] = useState(0);
  const [measuredTrough, setMeasuredTrough] = useState(0);
  const [timeDiff, setTimeDiff] = useState(0); // Time between peak and trough

  const isValidInput = measuredPeak > measuredTrough && timeDiff > 0;
  // New Individualized Ke Calculation
  const trueKe = isValidInput > 0? (Math.log(measuredPeak) - Math.log(measuredTrough)) / timeDiff :0 ;

  // Step 5: Calculate Half-life
  const halfLife = trueKe > 0? 0.693 / trueKe :0;
  // Now you can calculate the suggested interval (Tau)
  const suggestedTau = trueKe >0 ? (Math.log(targetCmax) - Math.log(targetCmin)) / trueKe :0;

  // Step 6: Back-calculating the true Cmax
  // Assuming true peak occurs at 1hr. 
  // If drawTime is 1.5, then tPrime is 0.5
  const tPrime = Math.max(0, drawTime ); 
  const extrapolatedCmax = measuredPeak * Math.exp(trueKe * tPrime);
  // Step 7: Calculate Expected Cmin base on the MANUAL interval
  // Formula: Cmin = Cmax * e^(-Ke * tau)
  const expectedCmin = extrapolatedCmax * Math.exp(-trueKe * manualTau);
  // Step 8: Calculate Patient-Specific Vd
  // We use the extrapolated Peak and the actual measured Trough
  const patientVd = (extrapolatedCmax > 0 && trueKe > 0 && manualTau > 0) 
    ? currentDose / (extrapolatedCmax * (1 - Math.exp(-trueKe * manualTau))) 
    : 0;
  // Clinical Check: Vd per kg
  const vdPerKg = weightToUse>0? patientVd / weightToUse:0;
  // Step 9: Calculate New Steady State Cmax
  // Using the formula: New Dose / (Vd * (1 - Math.exp(-trueKe * manualTau)))
  const newSteadyStateCmax = newTau >0 && patientVd >0 ? newDose / (patientVd * (1 - Math.exp(-trueKe * newTau))):0;
  // Calculate New Steady State Cmin (Trough) for completeness
  const newSteadyStateCmin =newSteadyStateCmax >0? newSteadyStateCmax * Math.exp(-trueKe * newTau):0;

  // 3. THE ONLY RETURN (This must be at the end of the function)
  return (
    <div style={{ padding: '20px', background: '#1b1f23', borderRadius: '10px', marginTop: '20px', border: '2px solid #8e44ad' }}>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '10px' }}>
        <button 
          type="button"
          className="no-print" 
          onClick={() => {
              const actualUserEmail = user && user.email ? user.email : 'anonymous-user';
              const trackingLabel = `${actualUserEmail}-printed-aminoglycoside`;
              logUserActivityQuietly(trackingLabel); 
              window.print();
          }}
          style={{
            padding: '10px 20px',
            backgroundColor: '#27ae60', 
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            fontWeight: 'bold',
            fontSize: '0.9rem',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            boxShadow: '0 4px 6px rgba(0,0,0,0.2)'
          }}
        >
          🖨️ Print PDF Report
        </button>
      </div>
      <h2 style = {{textAlign: 'center',color: '#3498db'}} >{aminoDrug?.toUpperCase()} TDM Worksheet</h2>
      <details style={{
            backgroundColor: '#1E293B',
            border: '1px solid #38BDF8',
            borderRadius: '8px',
            padding: '10px 15px',
            marginBottom: '20px',
            cursor: 'pointer'
        }}>
            <summary style={{ color: '#38BDF8', fontWeight: 'bold' }}>
            ⏱️ Check Clinical Sampling Protocol (Click to expand)
            </summary>
            <div style={{ marginTop: '12px', padding: '20px', background: 'rgba(0,0,0,0.2)', borderRadius: '12px', border: '1px solid #3d4e5d', fontSize: '0.9rem', color: '#F8FAFC' }}>
    
                {/* DYNAMIC TOP BADGE BAR DISPLAY */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '18px', borderBottom: '1px solid rgba(255,255,255,0.08)', paddingBottom: '12px' }}>
                  <span style={{ fontSize: '1.4rem' }}>⏱️</span>
                  <div>
                    <span style={{ fontSize: '0.75rem', fontWeight: 'bold', color: '#00d2ff', textTransform: 'uppercase', tracking: '0.5px', display: 'block' }}>
                      Protocol Sampling Reference
                    </span>
                    <h4 style={{ margin: 0, color: '#fff', fontSize: '1.1rem' }}>
                      {aminoDrug === 'amikacin' ? 'Amikacin Dosing & Sampling Matrix' : 'Gentamicin Dosing & Sampling Matrix'}
                    </h4>
                  </div>
                </div>

                {/* CONDITIONAL LOGIC PORT A: AMIKACIN MATRIX ENGINE */}
                {aminoDrug === 'amikacin' && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    <div style={gridRowStyle('rgba(52, 152, 219, 0.08)', '#3498db')}>
                      <div style={{ fontWeight: 'bold', width: '130px' }}>👶 Neonates:</div>
                      <div style={{ flex: 1 }}>Monitor at <strong> second dose (Q36H) / third dose (Q24H)</strong>. Draw Trough just before / within 30 min before dose (<span style={{color: '#3498db'}}>&lt;5 mcg/mL</span>). Draw Peak 30 min post-dose (<span style={{color: '#2ecc71'}}>20 - 30 mcg/mL</span>).</div>
                    </div>
                    
                    <div style={gridRowStyle('rgba(155, 89, 182, 0.08)', '#9b59b6')}>
                      <div style={{ fontWeight: 'bold', width: '130px' }}>🔁 MDD:</div>
                      <div style={{ flex: 1 }}>Monitor on **3rd dose**. Draw Trough just before / within 30 min before dose (<span style={{color: '#3498db'}}>&lt;10 mcg/mL</span>). Draw Peak 30 min post-dose (<span style={{color: '#2ecc71'}}>20 - 30 mcg/mL</span>).</div>
                    </div>

                    <div style={gridRowStyle('rgba(241, 196, 15, 0.06)', '#f1c40f')}>
                      <div style={{ fontWeight: 'bold', width: '130px' }}>🚀 SDD:</div>
                      <div style={{ flex: 1 }}>Monitor **after 1st dose**. Draw <strong>Post 2 Hours*</strong> (&lt;1 or &lt;5 mcg/mL). Draw <strong>Post 6 Hours*</strong> (<span style={{color: '#2ecc71'}}>&gt;35 mcg/mL</span>, targeting a peak:MIC ratio of 10:1**).</div>
                    </div>

                    <div style={gridRowStyle('rgba(231, 76, 60, 0.06)', '#e74c3c')}>
                      <div style={{ fontWeight: 'bold', width: '130px' }}>🏥 Dialysis:</div>
                      <div style={{ flex: 1 }}>Monitor at <strong>18 - 24 hours</strong>. Draw Trough **Pre-Hemodialysis (Pre HD)** (&lt;10 mcg/mL). Peak level monitoring is <span style={{color: '#95a5a6', fontStyle: 'italic'}}>Not Necessary</span>.</div>
                    </div>
                  </div>
                )}

                {/* CONDITIONAL LOGIC PORT B: GENTAMICIN MATRIX ENGINE */}
                {aminoDrug === 'gentamicin' && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    <div style={gridRowStyle('rgba(52, 152, 219, 0.08)', '#3498db')}>
                      <div style={{ fontWeight: 'bold', width: '130px' }}>👶 Neonates:</div>
                      <div style={{ flex: 1 }}>Monitor at <strong>second dose (Q36H) / third dose (Q24H)</strong>. Draw Trough just before / within 30 min before dose (<span style={{color: '#3498db'}}>&lt;1 mcg/mL</span>). Draw Peak 30 min post-dose (<span style={{color: '#2ecc71'}}>5 - 12 mcg/mL</span>).</div>
                    </div>

                    <div style={gridRowStyle('rgba(155, 89, 182, 0.08)', '#9b59b6')}>
                      <div style={{ fontWeight: 'bold', width: '130px' }}>🔁 MDD:</div>
                      <div style={{ flex: 1 }}>Monitor on **3rd dose**. Draw Trough just before / within 30 min before dose (<span style={{color: '#3498db'}}>&lt;2 mcg/mL</span>). Draw Peak 30 min post-dose (<span style={{color: '#2ecc71'}}>5 - 10 mcg/mL</span>).</div>
                    </div>

                    <div style={gridRowStyle('rgba(26, 188, 156, 0.08)', '#1abc9c')}>
                      <div style={{ fontWeight: 'bold', width: '130px' }}>❤️ Synergy:</div>
                      <div style={{ flex: 1 }}>Monitor on **3rd dose** (Endocarditis protocol). Draw Trough just before dose (&lt;1 mcg/mL). Draw Peak 30 min post-dose (<span style={{color: '#2ecc71'}}>3 - 5 mcg/mL</span>).</div>
                    </div>

                    <div style={gridRowStyle('rgba(241, 196, 15, 0.06)', '#f1c40f')}>
                      <div style={{ fontWeight: 'bold', width: '130px' }}>🚀 SDD:</div>
                      <div style={{ flex: 1 }}>Monitor **after 1st dose**. Draw <strong>Post 2 Hours*</strong> (&lt;1 mcg/mL). Draw <strong>Post 6 Hours*</strong> (<span style={{color: '#2ecc71'}}>10 - 30 mcg/mL**</span>).</div>
                    </div>

                    <div style={gridRowStyle('rgba(231, 76, 60, 0.06)', '#e74c3c')}>
                      <div style={{ fontWeight: 'bold', width: '130px' }}>🏥 Dialysis:</div>
                      <div style={{ flex: 1 }}>Monitor at <strong>18 - 24 hours</strong>. Draw Trough **Pre-Hemodialysis (Pre HD)** (&lt;2 mcg/mL). Peak level monitoring is <span style={{color: '#95a5a6', fontStyle: 'italic'}}>Not Necessary</span>.</div>
                    </div>
                  </div>
                )}

                {/* MANDATORY FOOTER ANNOTATIONS */}
                <div style={{ marginTop: '15px', paddingTop: '12px', borderTop: '1px solid rgba(255,255,255,0.08)', fontSize: '0.75rem', color: '#95a5a6', lineHeight: '1.5' }}>
                  <div>* <span style={{color: '#f1c40f'}}>SDD Protocol Note:</span> Alternatively, any two post-dose sampling levels drawn at least 2 half-life (t{1/2}) intervals apart can be utilized.</div>
                  <div style={{ marginTop: '4px' }}>** <span style={{color: '#2ecc71'}}>Target Rule:</span> Optimize peak-to-MIC ratio of **10:1** if baseline pathogen MIC data is available.</div>
                </div>

            </div>
        </details>
      {/* NEW: Input Section for Patient Data */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', background: '#1e272e', padding: '15px', borderRadius: '8px', marginBottom: '20px' }}>
        <div>
          <label style={{ fontSize: '0.8rem', fontWeight: 'bold' }}>Gender:</label><br/>
          <select value={gender} onChange={(e) => setGender(e.target.value)} style={{ width: '90%', padding: '8px' }}>
            <option value="male">Male</option>
            <option value="female">Female</option>
          </select>
        </div>
        <div>

        </div>
        <div>
          <label style={{ fontSize: '0.8rem', fontWeight: 'bold' }}>Actual Weight (kg):</label><br/>
          <input type="number" value={weight} onChange={(e) => {const val = e.target.value; setWeight(val === "" ? "" : Number(val))}} style={{ width: '85%', padding: '8px' }} />
        </div>
        <div>
          <label style={{ fontSize: '0.8rem', fontWeight: 'bold' }}>Height (cm) (Optional):</label><br/>
          <input type="number" value={height} onChange={(e) => setHeight(e.target.value)} style={{ width: '85%', padding: '8px' }} />
        </div>
        
      </div>
      {/* Updated: Weight Selection Summary Box displaying new variables */}
      <div style={{ background: '#2d3436', padding: '15px', borderRadius: '5px', marginBottom: '20px', borderLeft: '5px solid #8e44ad' }}>
        <div style={{color:'#bdc3c7'}}><strong>Dosing Weight Logic:</strong></div>
        <ul style={{ margin: '10px 0 0 0', fontSize: '0.9rem', listStyleType: 'none', paddingLeft: '5px',color:'#bdc3c7' }}>
          
          {heightNum > 0 && (
            <>
              <li style={{ marginBottom: '4px' }}>
                <span style={{ color: '#7f8c8d' }}>Protocol IBW: {gender === 'male' ? '50' : '45.5'} + 0.9 × ({height} - 152) = {ibw.toFixed(1)} kg</span>
              </li>
              <li style={{ marginBottom: '8px' }}>
                <span style={{ color: '#7f8c8d' }}>Protocol AdjBW: {ibw.toFixed(1)} + 0.4 × ({weightNum.toFixed(1)} - {ibw.toFixed(1)}) = {adjBw.toFixed(1)} kg</span>
              </li>
            </>
          )}

          <span style={{ fontSize: '0.65rem', color: '#bdc3c7', display: 'block' }}>CLINICAL DOSING WEIGHT ENGINE</span>
          <span style={{ fontSize: '1.1rem', fontWeight: 'bold', color: '#f1c40f' }}>{weightToUse.toFixed(1)} kg</span>
          
          <li style={{ marginTop: '4px', fontWeight: 'bold', color: '#38BDF8' }}>
            {weightSelectionMode}
          </li>
          
        </ul>
      </div>
      <div style={{ padding: '15px', background: '#1e272e', border: '1px solid #dee2e6', borderRadius: '10px', marginTop: '20px' }}>
        <h3 style={{ fontSize: '1rem', color: '#bdc3c7', borderBottom: '2px solid #3498db', paddingBottom: '5px' }}>
          Step 1: Estimate Creatinine Clearance (CrCl)
        </h3>
        <div>
          <label style={{ fontSize: '0.8rem', fontWeight: 'bold' }}>Age (years):</label><br/>
          <input 
            type="number" 
            value={age} 
            onChange={(e) => {const val = e.target.value; setAge(val === "" ? "" : Number(val))} }
            style={{ width: '87%', padding: '8px' }} 
          />
        </div>
        <div>
          <label style={{ fontSize: '0.8rem', fontWeight: 'bold' }}>Serum Creatinine:</label>
          <div>
            <input type="number" value={scr} onChange={(e) => setScr(Number(e.target.value))} style={{ width: '60%', padding: '8px' }}/>
            <select value={scrUnit} onChange={(e) => setScrUnit(e.target.value)} style={{ width: '27%', padding: '8px' }}>
              <option value="umol">umol/L</option>
              <option value="mgdl">mg/dL</option>
            </select>
          </div>
        </div>
        <div style={{ marginTop: '10px', fontSize: '0.9rem',color:'#bdc3c7'}}>
          <p>Formula used: <strong>((140 - Age) × BW × {crclConstant}) / SCr</strong></p>
          <div style={{ background: '#e8f4f8', padding: '10px', borderRadius: '5px', fontFamily: 'monospace',color:'#2d3436' }}>
            (({140 - age}) × {weightToUse.toFixed(1)} kg × {crclConstant}) / {normalizedScr.toFixed(0)} μmol/L 
            = <span style={{ fontSize: '1.1rem', fontWeight: 'bold', color: '#2980b9' }}>{crcl.toFixed(1)} ml/min</span>
          </div>
          
        </div>
      </div>
      <div style={{ padding: '20px', background: '#1e272e', border: '2px solid #27ae60', borderRadius: '10px', marginTop: '20px' }}>
        <h3 style={{ fontSize: '1.1rem', color: '#27ae60', borderBottom: '2px solid #27ae60', paddingBottom: '10px' }}>
          Step 2: Individualized Kinetics (Measured)
        </h3>
        {/* NEW INPUTS */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px', margin: '15px 0', background: '#434a43', padding: '10px', borderRadius: '5px' }}>
          <div>
            <label style={{ fontSize: '0.8rem' }}>Lab Measured Peak:</label>
            <input type="number" value={measuredPeak} onChange={(e) =>{const val = e.target.value; setMeasuredPeak(val === "" ? "" : Number(val))}} style={{ width: '90%' }} />
          </div>
          <div>
            <label style={{ fontSize: '0.8rem' }}>Lab Measured Trough:</label>
            <input type="number" value={measuredTrough} onChange={(e) =>{const val = e.target.value; setMeasuredTrough(val === "" ? "" : Number(val))}} style={{ width: '90%' }} />
          </div>
          <div>
            <label style={{ fontSize: '0.8rem' }}>Time Diff (Δt):</label>
            <input type="number" value={timeDiff} onChange={(e) =>{const val = e.target.value; setTimeDiff(val === "" ? "" : Number(val))}} style={{ width: '90%' }} />
          </div>
        </div>
        {/* FORMULA DISPLAY */}
        <div style={{ background: '#485248', padding: '15px', borderRadius: '5px' }}>
          <p style={{ margin: '0 0 10px 0', fontSize: '0.9rem', color: '#7f8c8d' }}>
            Formula: <strong>ln(Peak/Trough) / Δt</strong>
          </p>
          <div style={{ fontSize: '1.1rem', fontFamily: 'monospace' }}>
            ln({measuredPeak} / {measuredTrough}) / {timeDiff} = 
            <span style={{ fontWeight: 'bold', color: '#27ae60' }}> {trueKe.toFixed(4)} hr⁻¹</span>
          </div>
        </div>
        {/* Half-life Display */}
        <div style={{ textAlign: 'center', flex: 1 }}>
        <p style={{ fontSize: '1.5rem', color: '#7f8c8d', margin: 0 }}>Patient Half-life (t½):</p>
          <span style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#57c7b5' }}>{halfLife.toFixed(1)} hrs</span>
          <p style={{ fontSize: '1.5rem', color: '#bdc3c7', margin: 0 }}>Formula: 0.693 / {trueKe.toFixed(4)}</p>
        </div>                  

        <div style={{ marginTop: '20px', padding: '15px', background: '#2d3436', border: '1px solid #3498db', borderRadius: '10px' }}>
          <h3 style={{ fontSize: '1rem', color: '#3498db' }}>Step 3: Suggested Interval</h3>
          <p style={{ fontSize: '0.8rem', color: '#7f8c8d' }}>
            Targeting a Peak of <strong>{targetCmax}</strong> and Trough of <strong>{targetCmin}</strong>:
          </p>
          <div style={{ fontSize: '1.5rem', fontWeight: 'bold',color: '#fdf5e6' }}>
            Suggested τ: {suggestedTau.toFixed(1)} hours
          </div>
        </div>
        <div style={{ padding: '15px', background: '#2d3436', border: '1px solid #e67e22', borderRadius: '10px', marginTop: '20px' }}>
          <h3 style={{ fontSize: '1rem', color: '#e67e22', borderBottom: '2px solid #e67e22', paddingBottom: '5px' }}>
            Step 4: Estimated True Peak (Cmax)
          </h3>
          <div style={{ marginTop: '10px' }}>
            <label style={{ fontSize: '0.8rem', fontWeight: 'bold' }}>Time from <strong style={{color:'#3498db'}}>End of infusion</strong> to time of sampling(hrs):</label>
            <input 
              type="number" 
              value={drawTime} 
              onChange={(e) => {const val = e.target.value; setDrawTime(val === "" ? "" : Number(val))}} 
              style={{ width: '50px', marginLeft: '10px', padding: '5px' }} 
            />
            
            <div style={{ background: '#434a43', padding: '10px', borderRadius: '5px', marginTop: '10px' }}>
              <p style={{ margin: 0, fontSize: '0.9rem' }}>Formula: <strong>Cpost × e^(kₑ × t')</strong></p>
              <div style={{ fontSize: '1.1rem', fontFamily: 'monospace', marginTop: '5px' }}>
                {measuredPeak} × e^({trueKe.toFixed(4)} × {tPrime.toFixed(1)}) = 
                <span style={{ fontWeight: 'bold', color: '#e67e22' }}> {extrapolatedCmax.toFixed(1)} mcg/mL</span>
              </div>
            </div>
          </div>
        </div>
        <div style={{ padding: '20px', background: '#2d3436', border: '2px solid #c0392b', borderRadius: '10px', marginTop: '20px' }}>
          <h3 style={{ fontSize: '1.1rem', color: '#c0392b', borderBottom: '2px solid #c0392b', paddingBottom: '10px' }}>
            Step 5: Estimated True Trough (Cmin)
          </h3>
          <div style={{ background: '#434a43', padding: '15px', borderRadius: '8px', marginBottom: '15px' }}>
            <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '5px' }}>Adjust Dosing Interval (τ) in hours:</label>
            <input 
              type="number" 
              value={manualTau} 
              onChange={(e) => setManualTau(Number(e.target.value))} 
              style={{ width: '80px', fontSize: '1.2rem', padding: '5px', textAlign: 'center', border: '2px solid #c0392b', borderRadius: '5px' }} 
            />
            <span style={{ marginLeft: '10px', color: '#7f8c8d' }}>
              (Ideal Math was: {suggestedTau.toFixed(1)}h)
            </span>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
            <div style={{ textAlign: 'center', padding: '10px', background: '#434a43', border: '1px dashed #c0392b' }}>
              <p style={{ margin: 0, fontSize: '0.8rem', color: '#7f8c8d' }}>Predicted Peak:</p>
              <strong style={{ fontSize: '1.3rem' }}>{extrapolatedCmax.toFixed(1)} mcg/mL</strong>
            </div>
            <div style={{ textAlign: 'center', padding: '10px', background: '#434a43', border: '1px dashed #c0392b' }}>
              <p style={{ margin: 0, fontSize: '0.8rem', color: '#7f8c8d' }}>Predicted Trough:</p>
              <strong style={{ fontSize: '1.3rem', color: expectedCmin > targetCmin ? '#e74c3c' : '#27ae60' }}>
                {expectedCmin.toFixed(2)} mcg/mL
              </strong>
            </div>
          </div>
          <p style={{ fontSize: '0.8rem', color: '#7f8c8d', marginTop: '10px', textAlign: 'center' }}>
            {expectedCmin > targetCmin 
              ? "⚠️ Warning: Trough is higher than target. Consider increasing interval." 
              : "✅ Trough is within safe limits for this interval."}
          </p>
        </div>
        <div style={{ padding: '20px', background: '#2d3436', border: '2px solid #9b59b6', borderRadius: '10px', marginTop: '20px' }}>
          <h3 style={{ fontSize: '1.1rem', color: '#9b59b6', borderBottom: '2px solid #9b59b6', paddingBottom: '10px' }}>
            Step 6: Patient Volume of Distribution (Vd)
          </h3>
          <div style={{ background: '#434a43', padding: '12px', borderRadius: '8px', marginBottom: '15px' }}>
            <div>
              <label style={{ fontSize: '0.8rem', fontWeight: 'bold'}}>Current Dose (mg):</label><br/>
              <input type="number" value={currentDose} onChange={(e) =>{const val = e.target.value; setCurrentDose(val === "" ? "" : Number(val))}} style={{ width: '90%', padding: '8px'}} />
            </div>
            <div>
              <label style={{ fontSize: '0.8rem', fontWeight: 'bold'}}>Current Interval (τ):</label><br/>
              <input type="number" value={manualTau} onChange={(e) => {const val = e.target.value;setManualTau(val === "" ? "" : Number(val))}} style={{ width: '90%', padding: '8px'}} />
              <span style={{ fontSize: '0.8rem', color: '#7f8c8d' }}>
                  <div>
                    Mode: {aminoStrategy === 'extended' ? 'OD' : 'Multiple'}
                  </div>
              </span>
            </div>
            
          </div>
          <div style={{ background: '#434a43', border: '1px dashed #9b59b6', padding: '15px', borderRadius: '5px' }}>
            <p style={{ margin: '0 0 10px 0', fontSize: '0.9rem', color: '#7f8c8d' }}>
                Formula: <strong>Dose / (Cmax × (1 - e⁻ᵏᵉᵗᵃᵘ))</strong>
            </p>
            <div style={{ fontSize: '1.2rem', fontFamily: 'monospace' }}>
                {currentDose} / ({extrapolatedCmax.toFixed(1)} × (1 - e^-( {trueKe.toFixed(3)} × {manualTau} ))) = 
                <span style={{ fontWeight: 'bold', color: '#9b59b6' }}> {patientVd.toFixed(1)} Liters</span>
            </div>
            {/* Clinical Sanity Check */}
            <div style={{ marginTop: '12px', padding: '10px', background: '#4a5b5b', borderRadius: '4px', fontSize: '0.85rem', border: '1px solid #eee' }}>
              Normalized Vd: <strong>{vdPerKg.toFixed(2)} L/kg</strong>
              <span style={{ color: vdPerKg > 0.35 || vdPerKg < 0.2 ? '#e67e22' : '#27ae60', marginLeft: '10px', fontWeight: 'bold' }}>
                {vdPerKg > 0.35 ? "(High - Fluid Overload?)" : vdPerKg < 0.2 ? "(Low - Dehydrated?)" : "(Normal Range)"}
              </span>
            </div>
          </div>
        </div>
        <div style={{ padding: '20px', background: '#2d3436', color: '#fff', borderRadius: '10px', marginTop: '30px' }}>
          <h3 style={{ fontSize: '1.2rem', color: '#3498db', borderBottom: '1px solid #34495e', paddingBottom: '10px', marginTop: 0 }}>
            Step 7: Final Dosing Simulation
          </h3>
          {/* Inputs for Simulation */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
            <div>
              <label style={{ fontSize: '0.8rem', color: '#bdc3c7' }}>Test New Dose (mg):</label>
              <input 
                type="number" 
                value={newDose} 
                onChange={(e) => {const val = e.target.value; setNewDose(val === "" ? "" : Number(val))}} 
                style={{ width: '80%', padding: '10px', marginTop: '5px', fontSize: '1.1rem', fontWeight: 'bold', borderRadius: '5px', border: 'none' }} 
              />
            </div>
            <div>
              <label style={{ fontSize: '0.8rem', color: '#bdc3c7' }}>Test New Interval (τ):</label>
              <input 
                type="number" 
                value={newTau} 
                onChange={(e) => setNewTau(Number(e.target.value))} 
                style={{ width: '80%', padding: '10px', marginTop: '5px', fontSize: '1.1rem', fontWeight: 'bold', borderRadius: '5px', border: 'none' }} 
              />
            </div>
            {/* Transparency Section */}
             
            {/* 1. Peak Formula (Left Side) */}
            <div style={{ flex: 1, borderRight: '1px solid #455a64', paddingRight: '15px' }}>
              <p style={{ margin: '0 0 5px 0', color: '#3498db', fontWeight: 'bold' }}>1. Peak (Cmax) Logic:</p>
              <code>New Dose / [Vd × (1 - e^(-kₑτ))]</code>
              <div style={{ marginTop: '10px', fontStyle: 'italic', color: '#bdc3c7' }}>
                {newDose} / [{patientVd.toFixed(1)} × (1 - e^(-{trueKe.toFixed(4)} × {newTau}))] =
                <div style={{ color: '#2ecc71', fontSize: '1.1rem', fontWeight: 'bold', marginTop: '5px' }}>
                  {newSteadyStateCmax.toFixed(1)} mcg/mL
                </div>
              </div>
            </div>
            {/* 2. Trough Formula (Right Side) */}
            <div style={{ flex: 1, paddingLeft: '5px' }}>
              <p style={{ margin: '0 0 5px 0', color: '#3498db', fontWeight: 'bold' }}>2. Trough (Cmin) Logic:</p>
              <code>Cmax × e^(-kₑτ)</code>
              <div style={{ marginTop: '10px', fontStyle: 'italic', color: '#bdc3c7' }}>
                {newSteadyStateCmax.toFixed(1)} × e^(-{trueKe.toFixed(4)} × {newTau}) =
                <div style={{ color: newSteadyStateCmin > targetCmin ? '#e74c3c' : '#2ecc71', fontSize: '1.1rem', fontWeight: 'bold',marginTop: '5px' }}>
                  {newSteadyStateCmin.toFixed(2)} mcg/mL
                </div>
              </div>
            </div>
            
          </div>
          {/* Final Result Summary */}
          <div style={{ display: 'flex', justifyContent: 'space-around', textAlign: 'center', marginTop: '20px', borderTop: '2px solid #34495e', paddingTop: '15px' }}>
            <div>
              <p style={{ margin: 0, fontSize: '0.8rem', color: '#bdc3c7' }}>Final Predicted Peak:</p>
              <span style={{ fontSize: '1.8rem', fontWeight: 'bold', color: '#2ecc71' }}>{newSteadyStateCmax.toFixed(1)}</span>
            </div>
            <div>
              <p style={{ margin: 0, fontSize: '0.8rem', color: '#bdc3c7' }}>Final Predicted Trough:</p>
              <span style={{ fontSize: '1.8rem', fontWeight: 'bold', color: newSteadyStateCmin > targetCmin ? '#e74c3c' : '#2ecc71' }}>{newSteadyStateCmin.toFixed(2)}</span>
            </div>
          </div>
          {/* HANDBOOK DISCLAIMER LEGEND FOOTER FOR MEDICAL FILING */}
           
              <p style={{ margin: '12px 0 0 0', fontSize: '0.62rem', color: '#7f8c8d', fontStyle: 'italic', textAlign: 'center', lineHeight: '1.3' }}>
                *Evaluation derived from official Clinical Pharmacokinetics Handbook criteria. Patient-specific clinical conditions and absolute biomarker trends must always override automated population estimates prior to final intervention execution.
              </p>
            
        </div>

      </div>
    </div>
  );  

}
// Row design helper to standardize borders and color accents
const gridRowStyle = (bgColor, borderColor) => ({
  display: 'flex',
  alignItems: 'flex-start',
  padding: '12px',
  background: bgColor,
  borderLeft: `4px solid ${borderColor}`,
  borderRadius: '0 6px 6px 0',
  lineHeight: '1.4'
});
export default AminoglycosideCalculator;